import { useContext } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate, useParams, useLocation } from "react-router";
import { CustomButton } from './StyledComponents';
import { Col, Container, Row } from 'react-bootstrap';
import LocalizedStrings from 'react-localization';

import { PendingView } from './PendingView';
import { JobDetailsView } from './JobDetailsView';
import { addCss, getColors, getFont, getHeaderUrl, getLogoUrl, useQuery } from '../Utils';
import SettingsContext from '../SettingsContext';
import JobContext from '../JobContext';
import { JobProvider } from '../JobProvider';
import { JobList, Job, GeneralSettings, StatisticService, ClientLanguage, getClientLanguageAsString } from '../Api';
import { ErrorView } from './ErrorView';
import { ModalContainer } from 'reoverlay';
import { useCookieConsent, CookieConsentProvider } from '../CookieConsentContext';
import { ErrorViewType } from './Enums';

let locStrings = new LocalizedStrings({
    de:
    {
        messageSettingsLoaded:"Einstellungen werden geladen...",
        messageJobPostingLoaded:"Stellenausschreibung wird geladen...",
        back: "Zurück",
    },
    en:
    {
        messageSettingsLoaded:"Settings are loaded...",
        messageJobPostingLoaded:"Job posting is loaded...",
        back: "Back",
    }
});

interface JobViewInnerProps {
    tenant: string; 
    jobList: JobList;
    job: Job;
    onBackClick: () => void;
    previewMode: boolean;
    settings: GeneralSettings;
    onApplyClick: (jobId: string) => void;
  }
  
const JobViewInner = (props: JobViewInnerProps) => {
    const { consentGiven, openSettings, settingsDialogOpen, consentDialogOpen } = useCookieConsent();

    return (
        <>
            <Container fluid>
                <Container style={{ maxWidth: "800px", backgroundColor: "white", fontFamily: getFont(props.jobList, props.job) }}>
                    <Row>
                        <Col md={12} style={{ display: "flex", justifyContent: "right" }}>
                        </Col>
                        <Col md={12}>
                            {props.jobList.directIntegrationIframe &&
                                <Row>
                                    <Col lg={12}>
                                        <div className={"ListDetailsBackButton"} style={{ paddingTop: "30px", display: "flex", justifyContent: "left" }}>
                                            <CustomButton onClick={props.onBackClick} colors={getColors(props.jobList, props.job)}>{locStrings.back}</CustomButton>
                                        </div>
                                    </Col>
                                </Row>
                            }
                            {props.jobList.directIntegrationIframe && getHeaderUrl(props.jobList, props.job, props.settings) &&
                                <Row>
                                    <Col lg={12}>
                                        <div style={{ paddingTop: "30px", display: "flex", justifyContent: "left" }}>
                                        </div>
                                    </Col>
                                </Row>
                            }
                            <JobDetailsView
                                previewMode={props.previewMode}
                                job={props.job}
                                colors={getColors(props.jobList, props.job)}
                                logoUrl={getLogoUrl(props.jobList, props.job, props.settings)}
                                headerUrl={getHeaderUrl(props.jobList, props.job, props.settings)}
                                listId={props.jobList?.id}
                                fontFamily={getFont(props.jobList, props.job)}
                                onApplyClick={props.onApplyClick}
                                legalNoticeUrl={props.jobList.legalNoticeUrl}
                                dataSecurityUrl={props.jobList.dataSecurityUrl}
                                formalAddressPronoun={props.jobList.formalAddressPronoun} 
                                subtitleOnJobPosting={props.jobList.subtitleOnJobPosting} 
                                customlink1={props.jobList.customLink1}
                                customlink2={props.jobList.customLink2}
                                customlink3={props.jobList.customLink3}
                                customlink4={props.jobList.customLink4}
                                customlink5={props.jobList.customLink5}
                                customForm={props.jobList.customForm}
                                directIntegrationIframe={props.jobList.directIntegrationIframe}
                                submitApplicationSuccessHeader={props.jobList.submitApplicationSuccessHeader}
                                submitApplicationText={props.jobList.submitApplicationText}
                                clientLanguage={props.jobList.clientLanguage}
                                supportUrl={props.jobList.supportUrl}
                                supportEmail={props.jobList.supportEmail}
                                supportPhoneNumber={props.jobList.supportPhoneNumber}
                                supportCustomMessage={props.jobList.supportCustomMessage}/>
                        </Col>
                    </Row>
                </Container>
            </Container>
            {props.tenant.toLowerCase() === 'hering' && (props.jobList?.jobs[0].customForm !== "" ? props.jobList?.jobs[0].customForm : props.jobList?.customForm).toLowerCase() === 'standard' && !settingsDialogOpen && !consentDialogOpen &&
                <div
                    style={{
                    position: 'fixed',
                    bottom: 20,
                    left: 20,
                    fontSize: 24,
                    cursor: 'pointer',
                    zIndex: 1000
                    }}
                    onClick={openSettings}
                >
                    ⚙️
                </div>
            }
        </>
    );
}

export const JobView = () => {
    const query = useQuery();
    const previewMode = query.get("preview") !== null && query.get("preview") === "true";
    const { pathname } = useLocation();
    let { tenant = "", listName, id } = useParams();
    tenant = tenant === "" ? pathname.split('/')[1] : tenant; 
    const { isLoading: isSettingsLoading, settings } = useContext(SettingsContext);

    let navigate = useNavigate();

    function updateCounters(counterNames: string[]) {
        if (counterNames.length === 0) return;

        let counter = counterNames.shift();

        // force updateCounter calls to be processed synchronously
        StatisticService.updateCounter(tenant, id, counter).then(result => {
            updateCounters(counterNames);
            console.log();
        }).catch(() => {
            console.log();
        });
    }

    const onBackClick = () => {
        navigate(`/${tenant}/${listName}`, {});
    }

    const onApplyClick = (jobId: string) => {
        // force updateCounter calls (to "FormViews" and "FormApplyNowCount") to be processed synchronously
        updateCounters(["FormViews","FormApplyNowCount"]);
        navigate(`/${tenant}/${listName}/${jobId}/form`, {});
    }

    function getClientLanguage(jobList: JobList): string { 
        return getClientLanguageAsString(jobList.clientLanguage); 
    } 
    
    function setLanguage(jobList: JobList): void {
        switch(jobList.clientLanguage) { 
            case ClientLanguage.German: { 
               locStrings.setLanguage('de');
               break; 
            } 
            case ClientLanguage.English: { 
               locStrings.setLanguage('en'); 
               break; 
            } 
            // Otherwise "props.clientLanguage" is "ClientLanguage.BrowserSetting" and language used is browser setting dependent.
        } 
    }

    if (isSettingsLoading) {
        if (!previewMode) {
            updateCounters(["FormJobCount"]);
        }
        return <PendingView message={locStrings.messageSettingsLoaded} clientLanguage={ClientLanguage.BrowserSetting}></PendingView>;
    }

    return (
        <JobProvider>
            <JobContext.Consumer>
                {({ isLoading, jobList, job,error }) => isLoading ? <PendingView message={locStrings.messageJobPostingLoaded} clientLanguage={jobList?.clientLanguage}></PendingView> :
                (error) ? 
                    <>
                        <Container>
                            <Row>
                                <Col md={12}>
                                    <ErrorView message={error.message} description={error.description} errorViewType={ErrorViewType.JobViewErrorView}></ErrorView>
                                </Col>
                            </Row>
                        </Container >
                    </>
                    :
                (
                    <>
                        {tenant.toLowerCase() === 'hering' && (jobList?.jobs[0].customForm !== "" ? jobList?.jobs[0].customForm : jobList?.customForm).toLowerCase() === 'standard' ? (
                            <CookieConsentProvider>
                                {jobList.cssKey && addCss(`${settings.imagesUrl}/${tenant}.${jobList.cssKey}`)}
                                {setLanguage(jobList)}
                                <JobViewInner tenant={tenant} jobList={jobList} job={job} onBackClick={onBackClick} previewMode={previewMode} settings={settings} onApplyClick={onApplyClick} />
                                <ModalContainer />
                            </CookieConsentProvider>
                        ) : (
                            <>
                                {jobList.cssKey && addCss(`${settings.imagesUrl}/${tenant}.${jobList.cssKey}`)}
                                {setLanguage(jobList)}
                                <JobViewInner tenant={tenant} jobList={jobList} job={job} onBackClick={onBackClick} previewMode={previewMode} settings={settings} onApplyClick={onApplyClick} />
                            </>
                        )}
                        {/* <div>
                            {<small><b>Debug info: ClientLanguage: </b> {getClientLanguage(jobList)}</small>}
                        </div> */}
                    </>
                )}
            </JobContext.Consumer>
        </JobProvider>
    );
}
